import { motion } from "framer-motion";
import { CURRICULUM } from "@/lib/curriculum";
import LetterTile from "@/components/sections/azsystem/LetterTile";
import PhasePath from "@/components/sections/azsystem/PhasePath";
import GridBackground from "@/components/primitives/GridBackground";
import CinematicShield from "@/components/protection/CinematicShield";

/**
 * AZSystem — the doctrine map.
 *
 * variant="marketing" (default): full hero on landing page — eyebrow, headline,
 *                                 italic neon sub, paragraph, full-size grid,
 *                                 PhasePath strip, footer marker.
 * variant="dashboard":            compact in-portal embed — no eyebrow/headline,
 *                                 tighter grid, no PhasePath/footer; tiles reflect
 *                                 per-user state from `userProgress`.
 *
 * `userProgress` shape:
 *   {
 *     unlocked:  Set<string>    // entitled letters
 *     completed: Set<string>    // letters with `completed_at`
 *     active:    string | null  // user.last_active_module
 *     genesisIZ: boolean        // genesis_iz_early_access flag
 *   }
 */
export default function AZSystem({ variant = "marketing", userProgress = null }) {
    const isDashboard = variant === "dashboard";

    const tileStateFor = (entry) => {
        if (!userProgress) return null; // marketing variant — tile decides from `tier`
        const L = entry.letter;
        const isCore = entry.tier === "core";
        const isAdvanced = entry.tier === "advanced";
        const owned = userProgress.unlocked?.has(L);
        const done = userProgress.completed?.has(L);
        const active = userProgress.active === L;
        if (done) return "completed";
        if (active && owned) return "active";
        if (owned) return "available";
        // not owned
        if (isAdvanced && !userProgress.genesisIZ) return "upgrade-required";
        if (isCore) return "upgrade-required"; // missing core letter — entitlement issue
        return "available";
    };

    return (
        <section
            id={isDashboard ? "az-system-dashboard" : "az-system"}
            data-testid={isDashboard ? "az-system-dashboard" : "az-system-section"}
            data-variant={variant}
            className={`relative ${isDashboard ? "bg-transparent" : "bg-tca-deep"} overflow-hidden`}
        >
            {!isDashboard && <GridBackground fine />}
            {!isDashboard && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[820px] h-[420px] rounded-full"
                    style={{
                        background: "radial-gradient(ellipse, rgba(0,219,255,0.10), transparent 65%)",
                        filter: "blur(48px)",
                    }}
                />
            )}

            <CinematicShield intensity={isDashboard ? "low" : "medium"}>
                <div
                    className={
                        isDashboard
                            ? "relative z-10 max-w-[1280px] mx-auto px-1 sm:px-2 py-2"
                            : "relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-28 sm:py-40"
                    }
                >
                    {/* Marketing-only header */}
                    {!isDashboard && (
                        <div className="text-center max-w-3xl mx-auto">
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.6 }}
                                className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.36em] text-tca-cyan/85 mb-5"
                                data-testid="az-eyebrow"
                            >
                                // The Framework
                            </motion.div>
                            <motion.h2
                                initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
                                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                data-testid="az-headline"
                                className="font-display font-semibold text-[44px] leading-[1.04] sm:text-[68px] sm:leading-[1.0] lg:text-[84px] lg:leading-[0.98] tracking-[-0.025em] text-tca-ink"
                            >
                                The A–Z System.
                            </motion.h2>
                            <motion.div
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="font-display v3-italic neon-cyan text-2xl sm:text-3xl mt-3"
                            >
                                Build Intelligent Systems Correctly.
                            </motion.div>
                            <motion.p
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.7, delay: 0.3 }}
                                className="mt-7 sm:mt-9 text-tca-ink-muted text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
                                data-testid="az-paragraph"
                            >
                                Twenty-six disciplines. One doctrine. A–H is the operational core,
                                deployable today. I–Z is the advanced layer — integration,
                                optimization, scale, protection, infrastructure. Together they form
                                a living intelligence architecture.
                            </motion.p>
                        </div>
                    )}

                    {/* Grid */}
                    <div
                        data-testid={isDashboard ? "az-grid-dashboard" : "az-grid"}
                        className={
                            isDashboard
                                ? "mx-auto flex flex-wrap justify-center gap-x-2 gap-y-5 sm:gap-x-2.5 sm:gap-y-6 max-w-5xl"
                                : "mt-16 sm:mt-24 mx-auto flex flex-wrap justify-center gap-x-3 gap-y-7 sm:gap-x-4 sm:gap-y-9 lg:gap-x-5 max-w-5xl"
                        }
                    >
                        {CURRICULUM.map((entry, i) => (
                            <LetterTile
                                key={entry.letter}
                                letter={entry.letter}
                                title={entry.title}
                                summary={entry.summary}
                                tier={entry.tier}
                                index={i}
                                variant={variant}
                                state={tileStateFor(entry)}
                            />
                        ))}
                    </div>

                    {/* Marketing-only footer */}
                    {!isDashboard && <PhasePath />}
                    {!isDashboard && (
                        <div className="mt-12 sm:mt-16 flex items-center gap-4 max-w-2xl mx-auto">
                            <div className="h-px flex-1 bg-tca-ink-dim/40" />
                            <span className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/80">
                                26 disciplines · mapped end-to-end
                            </span>
                            <div className="h-px flex-1 bg-tca-ink-dim/40" />
                        </div>
                    )}
                </div>
            </CinematicShield>
        </section>
    );
}
